import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Box,
  Divider,
  ListItemText,
  ListItemIcon,
  useTheme,
  Skeleton,
  Button,
  Tooltip,
  Chip,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import {
  Notifications,
  NotificationsNone,
  Message,
  Favorite,
  Comment,
  PersonAdd,
  GroupAdd,
  EmojiEvents,
  Circle,
  DoneAll,
  Refresh,
  Settings,
  VolumeOff,
  VolumeUp
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { generateClient } from 'aws-amplify/api';
import { notificationsByUserIDAndCreatedAt } from '../../graphql/queries';
import { markNotificationRead, deleteNotification } from '../../graphql/mutations';
import { formatDistanceToNow } from 'date-fns';
import { eventNotificationService } from '../../services/eventNotificationService';
//import SwipeableNotification from './SwipeableNotification';

const client = generateClient();

function EnhancedNotificationBell() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const { unreadCount: chatUnreadCount } = useChat();
  
  // State management
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [nextToken, setNextToken] = useState(null);
  const [markingAsRead, setMarkingAsRead] = useState(new Set());
  const [mounted, setMounted] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [groupedNotifications, setGroupedNotifications] = useState([]);
  
  // Refs for cleanup and race condition prevention
  const retryTimeoutRef = useRef(null);
  const loadingRef = useRef(false);
  const mountedRef = useRef(true);
  const audioRef = useRef(null);

  // Calculate total unread count
  const totalUnread = useMemo(() => unreadCount + chatUnreadCount, [unreadCount, chatUnreadCount]);

  // Safe state update helper
  const safeSetState = useCallback((setter) => {
    if (mountedRef.current) {
      setter();
    }
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.volume = parseFloat(localStorage.getItem('notificationVolume') || '0.5');
      audioRef.current.play().catch((err) => {
        console.log('🔇 Sound play failed:', err);
      });
    }
  }, [soundEnabled]);

  // Group notifications by type and time
  const groupNotifications = useCallback((notifications) => {
    const groups = {};
    const timeWindow = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();
    
    notifications.forEach(notification => {
      try {
        const metadata = JSON.parse(notification.metadata || '{}');
        const notificationTime = new Date(notification.createdAt).getTime();
        
        if (now - notificationTime < timeWindow && ['like', 'comment', 'follow'].includes(metadata.type)) {
          const key = `${metadata.type}-${metadata.postID || 'general'}`;
          if (!groups[key]) {
            groups[key] = {
              type: metadata.type,
              notifications: [],
              actors: new Set()
            };
          }
          groups[key].notifications.push(notification);
          if (metadata.actorName) {
            groups[key].actors.add(metadata.actorName);
          }
        }
      } catch (error) {
        // Individual notification
      }
    });
    
    const grouped = [];
    Object.values(groups).forEach(group => {
      if (group.notifications.length > 1) {
        const actors = Array.from(group.actors);
        const count = group.notifications.length;
        let summary = '';
        
        switch (group.type) {
          case 'like':
            summary = actors.length === 1 
              ? `${actors[0]} and ${count - 1} others liked your post`
              : `${actors.slice(0, 2).join(', ')} and ${count - 2} others liked your post`;
            break;
          case 'comment':
            summary = `${actors.slice(0, 2).join(', ')} and others commented on your post`;
            break;
          case 'follow':
            summary = `${actors.slice(0, 2).join(', ')} and others started following you`;
            break;
        }
        
        grouped.push({
          id: `group-${group.type}-${Date.now()}`,
          isGrouped: true,
          type: group.type,
          message: summary,
          createdAt: group.notifications[0].createdAt,
          isRead: group.notifications.every(n => n.isRead),
          notifications: group.notifications
        });
      } else {
        grouped.push(...group.notifications);
      }
    });
    
    // Add ungrouped notifications
    notifications.forEach(notification => {
      try {
        const metadata = JSON.parse(notification.metadata || '{}');
        if (!['like', 'comment', 'follow'].includes(metadata.type)) {
          grouped.push(notification);
        }
      } catch (error) {
        grouped.push(notification);
      }
    });
    
    return grouped.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, []);

  // Load notifications with proper error handling
  const loadNotifications = useCallback(async (isRefresh = false) => {
    if (!user?.id || loadingRef.current) return;
    
    loadingRef.current = true;
    safeSetState(() => {
      setLoading(true);
      setError(null);
    });
    
    try {
      const token = isRefresh ? null : nextToken;
      const response = await client.graphql({
        query: notificationsByUserIDAndCreatedAt,
        variables: {
          userID: user.id,
          limit: 20,
          nextToken: token,
          sortDirection: 'DESC'
        }
      });

      if (!mountedRef.current) return;

      const newNotifications = response.data?.notificationsByUserIDAndCreatedAt?.items || [];
      const newNextToken = response.data?.notificationsByUserIDAndCreatedAt?.nextToken;
      
      safeSetState(() => {
        if (isRefresh) {
          setNotifications(newNotifications);
          setUnreadCount(newNotifications.filter(n => !n.isRead).length);
        } else {
          setNotifications(prev => [...prev, ...newNotifications]);
          setUnreadCount(prev => prev + newNotifications.filter(n => !n.isRead).length);
        }
        
        setNextToken(newNextToken);
        setHasMore(!!newNextToken);
        setGroupedNotifications(groupNotifications(newNotifications));
      });
      
    } catch (error) {
      console.error('Error loading notifications:', error);
      
      if (mountedRef.current) {
        safeSetState(() => {
          setError('Unable to load notifications');
        });
        
        // Retry with exponential backoff
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            loadNotifications(isRefresh);
          }
        }, 5000);
      }
    } finally {
      loadingRef.current = false;
      safeSetState(() => setLoading(false));
    }
  }, []);

  // Initialize component
  useEffect(() => {
    mountedRef.current = true;
    setMounted(true);
    
    // Initialize audio
    audioRef.current = new Audio('/sounds/notification.mp3');
    audioRef.current.volume = 0.5;
    
    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Load notifications when user changes and setup real-time subscription
  useEffect(() => {
    if (user?.id && mounted) {
      console.log('🔔 Setting up notifications for user:', user.id);
      loadNotifications(true);
      
      // Setup real-time subscription with better error handling
      try {
        const subscription = eventNotificationService.subscribe(user.id, (newNotification) => {
          console.log('🔔 New notification received:', newNotification);
          
          // Play sound immediately
          if (soundEnabled && audioRef.current) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(err => console.log('Sound failed:', err));
          }
          
          safeSetState(() => {
            setNotifications(prev => [newNotification, ...prev]);
            if (!newNotification.isRead) {
              setUnreadCount(prev => prev + 1);
            }
          });
        });
        
        return () => {
          console.log('🔔 Cleaning up notification subscription');
          eventNotificationService.unsubscribe(user.id);
        };
      } catch (error) {
        console.error('🔔 Failed to setup notification subscription:', error);
        // Fallback to periodic refresh if subscription fails
        const fallbackInterval = setInterval(() => {
          if (mountedRef.current && !loadingRef.current) {
            console.log('🔔 Fallback refresh triggered');
            loadNotifications(true);
          }
        }, 60000); // Refresh every minute as fallback
        
        return () => {
          clearInterval(fallbackInterval);
        };
      }
    }
  }, [user?.id, mounted, safeSetState, playNotificationSound]);

  // Fallback refresh - only when subscription fails
  useEffect(() => {
    if (!user?.id) return;
    
    // Minimal fallback refresh every 5 minutes
    const interval = setInterval(() => {
      if (mountedRef.current && !loadingRef.current) {
        loadNotifications(true);
      }
    }, 300000); // 5 minutes
    
    return () => clearInterval(interval);
  }, [user?.id]);

  // Refresh on window focus (with throttling)
  useEffect(() => {
    let lastFocusRefresh = 0;
    
    const handleFocus = () => {
      const now = Date.now();
      // Throttle focus refreshes to once per minute
      if (now - lastFocusRefresh > 60000 && user?.id && mountedRef.current && !loadingRef.current) {
        lastFocusRefresh = now;
        loadNotifications(true);
      }
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user?.id]);

  // Update grouped notifications when notifications change
  useEffect(() => {
    if (notifications.length > 0) {
      setGroupedNotifications(groupNotifications(notifications));
    }
  }, [notifications, groupNotifications]);

  // Handle notification click
  const handleNotificationClick = useCallback(async (notification) => {
    if (notification.isGrouped) {
      // Handle grouped notifications
      const firstNotification = notification.notifications[0];
      try {
        const metadata = JSON.parse(firstNotification.metadata || '{}');
        handleClose();
        
        switch (metadata.type) {
          case 'like':
          case 'comment':
            navigate(`/post/${metadata.postID}`);
            break;
          case 'follow':
            navigate('/profile');
            break;
        }
      } catch (error) {
        console.error('Error handling grouped notification:', error);
      }
      return;
    }

    if (!notification.isRead && !markingAsRead.has(notification.id)) {
      safeSetState(() => {
        setMarkingAsRead(prev => new Set([...prev, notification.id]));
      });
      
      try {
        await client.graphql({
          query: markNotificationRead,
          variables: { id: notification.id }
        });
        
        safeSetState(() => {
          setNotifications(prev => 
            prev.map(n => 
              n.id === notification.id ? { ...n, isRead: true } : n
            )
          );
          setUnreadCount(prev => Math.max(0, prev - 1));
        });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      } finally {
        safeSetState(() => {
          setMarkingAsRead(prev => {
            const newSet = new Set(prev);
            newSet.delete(notification.id);
            return newSet;
          });
        });
      }
    }

    // Navigate based on notification metadata
    try {
      const metadata = JSON.parse(notification.metadata || '{}');
      handleClose();
      
      switch (metadata.type) {
        case 'message':
          navigate(metadata.conversationID ? `/chat/${metadata.conversationID}` : '/chat');
          break;
        case 'like':
        case 'comment':
          navigate(`/post/${metadata.postID}`);
          break;
        case 'follow':
          navigate(`/user/${metadata.followerID}`);
          break;
        case 'group_invitation':
          navigate('/chat?showInvitations=true');
          break;
        case 'achievement':
          navigate('/achievements');
          break;
        default:
          // Handle legacy notifications
          if (notification.message.includes('started following')) {
            navigate('/profile');
          } else if (notification.message.includes('invited you to join')) {
            navigate('/chat?showInvitations=true');
          } else if (notification.message.includes('Achievement Unlocked')) {
            navigate('/achievements');
          }
      }
    } catch (error) {
      console.error('Error parsing notification metadata:', error);
      handleClose();
    }
  }, [navigate, markingAsRead, safeSetState]);

  // Get notification icon
  const getNotificationIcon = useCallback((notification) => {
    if (notification.isGrouped) {
      switch (notification.type) {
        case 'like':
          return <Favorite fontSize="small" color="error" />;
        case 'comment':
          return <Comment fontSize="small" color="info" />;
        case 'follow':
          return <PersonAdd fontSize="small" color="success" />;
        default:
          return <Circle fontSize="small" color="disabled" />;
      }
    }

    try {
      const metadata = JSON.parse(notification.metadata || '{}');
      switch (metadata.type) {
        case 'message':
          return <Message fontSize="small" color="primary" />;
        case 'like':
          return <Favorite fontSize="small" color="error" />;
        case 'comment':
          return <Comment fontSize="small" color="info" />;
        case 'follow':
          return <PersonAdd fontSize="small" color="success" />;
        case 'group_invitation':
          return <GroupAdd fontSize="small" color="warning" />;
        case 'achievement':
          return <EmojiEvents fontSize="small" sx={{ color: '#FFD700' }} />;
        default:
          return <Circle fontSize="small" color="disabled" />;
      }
    } catch (error) {
      return <Circle fontSize="small" color="disabled" />;
    }
  }, []);

  // Format notification time
  const formatTime = useCallback((timestamp) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
      return 'Recently';
    }
  }, []);

  // Handle menu open/close
  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    console.log('🔄 Manual refresh triggered');
    loadNotifications(true);
  }, [loadNotifications]);

  // Handle mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    const unreadNotifications = notifications.filter(n => !n.isRead);
    
    if (unreadNotifications.length === 0) return;
    
    try {
      await Promise.all(
        unreadNotifications.map(notification =>
          client.graphql({
            query: markNotificationRead,
            variables: { id: notification.id }
          })
        )
      );
      
      safeSetState(() => {
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [notifications, safeSetState]);

  // Filter notifications based on active tab
  const filteredNotifications = useMemo(() => {
    switch (activeTab) {
      case 1: // Unread
        return groupedNotifications.filter(n => !n.isRead);
      case 2: // Read
        return groupedNotifications.filter(n => n.isRead);
      default: // All
        return groupedNotifications;
    }
  }, [groupedNotifications, activeTab]);

  if (!user || !mounted) {
    return null;
  }

  return (
    <Box>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleClick}
          sx={{
            color: theme.palette.mode === 'dark' ? '#fff' : 'inherit',
            position: 'relative'
          }}
        >
          <Badge 
            badgeContent={totalUnread} 
            color="error" 
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                animation: totalUnread > 0 ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.1)' },
                  '100%': { transform: 'scale(1)' }
                }
              }
            }}
          >
            {totalUnread > 0 ? <Notifications /> : <NotificationsNone />}
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.4)'
              : '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ p: 2, pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              Notifications
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={soundEnabled ? "Mute notifications" : "Unmute notifications"}>
                <IconButton size="small" onClick={() => setSoundEnabled(!soundEnabled)}>
                  {soundEnabled ? <VolumeUp fontSize="small" color="primary" /> : <VolumeOff fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title={loading ? "Loading..." : "Refresh notifications"}>
                <IconButton 
                  size="small" 
                  onClick={handleRefresh} 
                  disabled={loading}
                  sx={{ 
                    color: loading ? 'text.disabled' : 'primary.main',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  {loading ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
                </IconButton>
              </Tooltip>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton size="small" onClick={handleMarkAllAsRead}>
                    <DoneAll fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          
          {totalUnread > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={`${totalUnread} unread`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
              {chatUnreadCount > 0 && (
                <Chip 
                  label={`${chatUnreadCount} messages`} 
                  size="small" 
                  color="secondary" 
                  variant="outlined"
                />
              )}
            </Box>
          )}
        </Box>

        {/* Tabs */}
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`All (${groupedNotifications.length})`} />
          <Tab label={`Unread (${groupedNotifications.filter(n => !n.isRead).length})`} />
          <Tab label="Read" />
        </Tabs>

        {/* Error state */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
            <Button size="small" onClick={handleRefresh} sx={{ ml: 1 }}>
              Retry
            </Button>
          </Alert>
        )}

        {/* Chat unread indicator */}
        {chatUnreadCount > 0 && (
          <MenuItem
            onClick={() => {
              handleClose();
              navigate('/chat');
            }}
            sx={{
              bgcolor: theme.palette.mode === 'dark' 
                ? 'rgba(255, 193, 7, 0.1)' 
                : 'rgba(25, 118, 210, 0.1)',
              borderLeft: `4px solid ${theme.palette.primary.main}`
            }}
          >
            <ListItemIcon>
              <Message fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={`${chatUnreadCount} unread message${chatUnreadCount > 1 ? 's' : ''}`}
              secondary="Click to open chat"
            />
          </MenuItem>
        )}

        {/* Notifications list */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {loading && filteredNotifications.length === 0 ? (
            // Loading skeletons
            Array.from({ length: 3 }).map((_, index) => (
              <MenuItem key={index} disabled>
                <ListItemIcon>
                  <Skeleton variant="circular" width={24} height={24} />
                </ListItemIcon>
                <ListItemText
                  primary={<Skeleton variant="text" width="80%" />}
                  secondary={<Skeleton variant="text" width="40%" />}
                />
              </MenuItem>
            ))
          ) : filteredNotifications.length === 0 ? (
            <MenuItem disabled>
              <ListItemText 
                primary="No notifications" 
                secondary="You're all caught up!"
                sx={{ textAlign: 'center' }}
              />
            </MenuItem>
          ) : (
            filteredNotifications.slice(0, 15).map((notification) => (
              <MenuItem
                key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    bgcolor: !notification.isRead
                      ? (theme.palette.mode === 'dark' 
                          ? 'rgba(255, 193, 7, 0.1)' 
                          : 'rgba(25, 118, 210, 0.1)')
                      : 'transparent',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.1)' 
                        : 'rgba(0, 0, 0, 0.04)'
                    },
                    opacity: markingAsRead.has(notification.id) ? 0.6 : 1,
                    position: 'relative'
                  }}
                >
                <ListItemIcon>
                  {getNotificationIcon(notification)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          fontWeight: !notification.isRead ? 600 : 400,
                          lineHeight: 1.3
                        }}
                      >
                        {notification.message}
                      </Typography>
                      {!notification.isRead && (
                        <Circle sx={{ fontSize: 8, color: 'primary.main' }} />
                      )}
                      {notification.isGrouped && (
                        <Chip 
                          label={notification.notifications.length} 
                          size="small" 
                          sx={{ fontSize: '0.7rem', height: 16 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(notification.createdAt)}
                    </Typography>
                  }
                />
                {markingAsRead.has(notification.id) && (
                  <CircularProgress 
                    size={16} 
                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                  />
                )}
                </MenuItem>              
            ))
          )}
        </Box>

        {/* Load more */}
        {hasMore && filteredNotifications.length >= 15 && [
          <Divider key="load-more-divider" />,
          <MenuItem key="load-more-item" onClick={() => loadNotifications(false)} disabled={loading}>
            <ListItemText 
              primary={loading ? "Loading..." : "Load more notifications"}
              sx={{ textAlign: 'center' }}
            />
          </MenuItem>
        ]}

        {/* Footer - Disabled until ProductionNotificationProvider is ready
        {filteredNotifications.length > 0 && [
          <Divider key="footer-divider" />,
          <MenuItem 
            key="footer-item"
            onClick={() => {
              handleClose();
              navigate('/notifications');
            }}
          >
            <ListItemText 
              primary="View all notifications"
              sx={{ textAlign: 'center', color: 'primary.main' }}
            />
          </MenuItem>
        ]} */}
      </Menu>
    </Box>
  );
}

export default EnhancedNotificationBell;